// React
import { useState, useCallback, useMemo, useEffect } from "react";

// Third-party
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  List,
  Loader2,
  Map,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from "lucide-react";

// Hooks & Contexts
import { useListings, useTranslation, useIsMobile } from "@/hooks";
import { useAuth } from "@/contexts";

// Components
import { FilterPanel } from "@/components/filters";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ListingCard, ListingsMap } from "@/components/listings";
import { SearchBar } from "@/components/search";
import { Header } from "@/components/Header";

// Constants
import { AppRoutes, DEFAULT_FILTER_STATE } from "@/constants";

// Types
import type { SearchCriteria } from "@/components/search";
import type { FilterState } from "@/constants";
import type { Listing, ListingsSearchParams } from "@/types";

/**
 * Convert FilterState (UI) to ListingsSearchParams (API)
 */
function filterStateToSearchParams(filters: FilterState): ListingsSearchParams {
  const params: ListingsSearchParams = {};

  // Price range
  if (filters.rentMin > 0) params.rentMin = filters.rentMin;
  if (filters.rentMax < 500000) params.rentMax = filters.rentMax;
  params.includeManagementFee = filters.includeManagementFee;

  // Size range
  if (filters.sizeMin > 0) params.sizeMin = filters.sizeMin;
  if (filters.sizeMax < 200) params.sizeMax = filters.sizeMax;

  // Room layouts
  if (filters.roomLayout.length > 0) {
    params.roomLayout = filters.roomLayout.join(",");
  }

  // Property types
  if (filters.propertyType.length > 0) {
    params.propertyType = filters.propertyType.join(",");
  }

  // Building/location filters
  if (filters.buildingAge !== "any")
    params.buildingAge =
      filters.buildingAge as ListingsSearchParams["buildingAge"];
  if (filters.floor !== "any")
    params.floor = filters.floor as ListingsSearchParams["floor"];
  if (filters.walkToStation !== "any")
    params.walkToStation =
      filters.walkToStation as ListingsSearchParams["walkToStation"];
  if (filters.keyMoney !== "any")
    params.keyMoney = filters.keyMoney as ListingsSearchParams["keyMoney"];
  if (filters.deposit !== "any")
    params.deposit = filters.deposit as ListingsSearchParams["deposit"];

  // Amenities
  if (filters.airConditioning) params.airConditioning = true;
  if (filters.washerHookup) params.washerHookup = true;
  if (filters.washerDryerInUnit) params.washerDryerInUnit = true;
  if (filters.bathToiletSeparate) params.bathToiletSeparate = true;
  if (filters.balcony) params.balcony = true;
  if (filters.flooringWood) params.flooringWood = true;
  if (filters.internetIncluded) params.internetIncluded = true;
  if (filters.gasStove) params.gasStove = true;
  if (filters.ihCooktop) params.ihCooktop = true;
  if (filters.elevator) params.elevator = true;
  if (filters.autoLock) params.autoLock = true;
  if (filters.deliveryBox) params.deliveryBox = true;
  if (filters.parking) params.parking = true;
  if (filters.bicycleParking) params.bicycleParking = true;
  if (filters.petsAllowed) params.petsAllowed = true;
  if (filters.instrumentsOK) params.instrumentsOK = true;
  if (filters.foreignerFriendly) params.foreignerFriendly = true;
  if (filters.noGuarantorNeeded) params.noGuarantorNeeded = true;
  if (filters.moveInWithin30Days) params.moveInWithin30Days = true;

  return params;
}

/**
 * Parse initial filters from URL search params
 */
function parseInitialFilters(urlParams: URLSearchParams): Partial<FilterState> {
  const partial: Partial<FilterState> = {};

  // Note: prefecture, city, ward are passed via URL but not part of FilterState
  // They are handled separately in effectiveSearchParams

  // Parse price range
  const rentMin = urlParams.get("rentMin");
  if (rentMin) partial.rentMin = parseInt(rentMin, 10);

  const rentMax = urlParams.get("rentMax");
  if (rentMax) partial.rentMax = parseInt(rentMax, 10);

  // Parse room layout
  const roomLayout = urlParams.get("roomLayout");
  if (roomLayout) {
    partial.roomLayout = roomLayout.split(",").filter(Boolean);
  }

  return partial;
}

export function OverviewPage() {
  const t = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Parse initial search criteria from URL (only once on mount)
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(() => {
    const prefecture = urlSearchParams.get("prefecture") || "Tokyo";
    const city = urlSearchParams.get("city") || "Tokyo";
    const ward = urlSearchParams.get("ward") || "";
    const moveInDateStr = urlSearchParams.get("moveInDate");
    const occupantsStr = urlSearchParams.get("occupants");

    return {
      prefecture,
      city,
      ward,
      moveInDate: moveInDateStr ? new Date(moveInDateStr) : null,
      occupants: occupantsStr ? parseInt(occupantsStr, 10) : 1,
    };
  });

  // Parse initial filters from URL (only once on mount)
  const [filters, setFilters] = useState<FilterState>(() => {
    const parsed = parseInitialFilters(urlSearchParams);
    return { ...DEFAULT_FILTER_STATE, ...parsed };
  });
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);

  // Determine if we should use mobile map mode (fullscreen, no scroll)
  const isMobileMapMode = isMobile && viewMode === "map";

  // Prevent body scroll when in mobile map mode
  useEffect(() => {
    if (isMobileMapMode) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isMobileMapMode]);

  // Convert UI filters to API params
  const searchParams = useMemo(
    () => filterStateToSearchParams(filters),
    [filters],
  );

  // Add location from URL to search params
  const effectiveSearchParams = useMemo(() => {
    const params = { ...searchParams };
    const prefecture = urlSearchParams.get("prefecture");
    const city = urlSearchParams.get("city");
    const ward = urlSearchParams.get("ward");
    if (prefecture) params.prefecture = prefecture;
    if (city) params.city = city;
    if (ward) params.ward = ward;
    return params;
  }, [searchParams, urlSearchParams]);

  // Use listings hook with initial params
  const {
    listings,
    isLoading,
    isLoadingMore,
    error,
    totalCount,
    hasMore,
    loadMore,
    refresh,
    updateFilters: updateApiFilters,
    resetFilters: resetApiFilters,
  } = useListings({
    initialFilters: effectiveSearchParams,
    syncToUrl: true,
    autoFetch: true,
  });

  // Handle filter changes - check if it's a reset (DEFAULT_FILTER_STATE)
  const handleFiltersChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);

      // Check if this is a reset to defaults
      const isReset =
        JSON.stringify(newFilters) === JSON.stringify(DEFAULT_FILTER_STATE);

      if (isReset) {
        // Use hook's resetFilters to properly clear all filter state
        resetApiFilters();
      } else {
        // Normal filter change - convert and preserve location
        const newParams = filterStateToSearchParams(newFilters);
        const prefecture = urlSearchParams.get("prefecture");
        const city = urlSearchParams.get("city");
        const ward = urlSearchParams.get("ward");
        if (prefecture) newParams.prefecture = prefecture;
        if (city) newParams.city = city;
        if (ward) newParams.ward = ward;
        updateApiFilters(newParams);
      }
    },
    [updateApiFilters, resetApiFilters, urlSearchParams],
  );

  // Handle search criteria change from SearchBar
  const handleSearchCriteriaChange = useCallback(
    (newCriteria: SearchCriteria) => {
      setSearchCriteria(newCriteria);

      // Update URL and refetch with new location
      const newParams = filterStateToSearchParams(filters);
      newParams.prefecture = newCriteria.prefecture;
      newParams.city = newCriteria.city;
      if (newCriteria.ward) newParams.ward = newCriteria.ward;

      // Note: moveInDate and occupants are for UI context, not currently filtered on backend
      // Future: add availableFrom filter based on moveInDate

      updateApiFilters(newParams);

      // Update URL to reflect new search criteria
      const urlParams = new URLSearchParams();
      urlParams.set("prefecture", newCriteria.prefecture);
      urlParams.set("city", newCriteria.city);
      if (newCriteria.ward) urlParams.set("ward", newCriteria.ward);
      if (newCriteria.moveInDate) {
        const dateStr = newCriteria.moveInDate.toISOString().split("T")[0];
        if (dateStr) urlParams.set("moveInDate", dateStr);
      }
      if (newCriteria.occupants > 1) {
        urlParams.set("occupants", String(newCriteria.occupants));
      }

      navigate(`/overview?${urlParams.toString()}`, { replace: true });
    },
    [filters, updateApiFilters, navigate],
  );

  const handleBack = () => {
    navigate(-1);
  };

  const handleListingClick = (listing: Listing) => {
    // Navigate to listing detail page
    navigate(`/listing/${listing.id}`);
  };

  const handleListingHover = (listing: Listing | null) => {
    setSelectedListing(listing);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const scrolledToBottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 200;

      if (scrolledToBottom && hasMore && !isLoadingMore) {
        loadMore();
      }
    },
    [hasMore, isLoadingMore, loadMore],
  );

  // Back button component for header left slot
  const backButton = (
    <button
      onClick={handleBack}
      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label={t("back")}
    >
      <ArrowLeft className="w-5 h-5 text-gray-600" />
    </button>
  );

  // Mobile map view - fullscreen layout
  if (isMobileMapMode) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
        {/* Compact Header for mobile map */}
        <Header
          position="relative"
          variant="solid"
          showShadow
          showBorder
          leftContent={backButton}
          rightContent={
            <div className="flex items-center gap-2">
              {/* Filter button */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                aria-label={t("filtersTitle")}
              >
                <SlidersHorizontal className="w-4 h-4" />
              </button>
              {/* Switch to list view */}
              <button
                onClick={() => setViewMode("list")}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
              >
                <List className="w-4 h-4" />
                <span>{t("overviewListView")}</span>
              </button>
            </div>
          }
        />

        {/* Results count bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {isLoading
              ? t("overviewLoading")
              : `${totalCount} ${t("overviewResults")}`}
          </span>
          <button
            onClick={refresh}
            disabled={isLoading}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Refresh"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-600 ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Mobile Filters Drawer - Also available in map mode */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-[60]">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowMobileFilters(false)}
            />

            {/* Drawer */}
            <div className="absolute left-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClose={() => setShowMobileFilters(false)}
              />
            </div>
          </div>
        )}

        {/* Map fills remaining space */}
        <div className="flex-1 min-h-0">
          <ListingsMap
            listings={listings}
            selectedListing={selectedListing}
            onListingSelect={handleListingClick}
            onListingHover={handleListingHover}
            onListingClick={handleListingClick}
            isMobile={true}
            className="h-full"
          />
        </div>
      </div>
    );
  }

  // Standard layout (list view or desktop map view)
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <Header
        position="sticky"
        variant="solid"
        showShadow
        showBorder
        leftContent={backButton}
        rightContent={
          <div className="flex items-center gap-4">
            <LanguageSwitcher className="text-gray-700" />
            {user ? (
              <Link
                to={AppRoutes.dashboard}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {t("dashboard")}
              </Link>
            ) : (
              <Link
                to={AppRoutes.login}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {t("signIn")}
              </Link>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Bar - Above filters and results */}
        <div className="mb-6">
          <SearchBar
            criteria={searchCriteria}
            onChange={handleSearchCriteriaChange}
          />
        </div>

        <div className="flex gap-6">
          {/* Left Sidebar - Filters (Desktop) */}
          <aside className="hidden lg:block w-80 shrink-0">
            <div className="rounded-2xl shadow-lg border border-gray-200 bg-white">
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            </div>
          </aside>

          {/* Mobile Filters Drawer */}
          {showMobileFilters && (
            <div className="fixed inset-0 z-50 lg:hidden">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowMobileFilters(false)}
              />

              {/* Drawer */}
              <div className="absolute left-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
                <FilterPanel
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClose={() => setShowMobileFilters(false)}
                />
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {/* Mobile Controls Bar - Only visible on mobile */}
            <div className="lg:hidden flex items-center justify-between gap-2 mb-4">
              {/* Mobile filter toggle */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 bg-white"
                aria-label={t("filtersTitle")}
              >
                <SlidersHorizontal className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {t("filtersTitle")}
                </span>
              </button>

              {/* Mobile View Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "list"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {t("overviewListView")}
                  </span>
                </button>
                <button
                  onClick={() => setViewMode("map")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "map"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Map className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {t("overviewMapView")}
                  </span>
                </button>
              </div>
            </div>

            {/* Results Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
              <div className="flex items-center justify-between gap-4">
                {/* Left: Title + Count */}
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                    {t("overviewResultsTitle")}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {isLoading ? (
                      t("overviewLoading")
                    ) : error ? (
                      <span className="text-red-600">{error}</span>
                    ) : (
                      `${totalCount} ${t("overviewResults")}`
                    )}
                  </p>
                </div>

                {/* Right: View toggle (desktop) + Refresh */}
                <div className="flex items-center gap-3 shrink-0">
                  {/* Desktop View Mode Toggle */}
                  <div className="hidden lg:flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                    <button
                      onClick={() => setViewMode("list")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        viewMode === "list"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <List className="w-4 h-4" />
                      <span>{t("overviewListView")}</span>
                    </button>
                    <button
                      onClick={() => setViewMode("map")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        viewMode === "map"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <Map className="w-4 h-4" />
                      <span>{t("overviewMapView")}</span>
                    </button>
                  </div>

                  {/* Refresh button */}
                  <button
                    onClick={refresh}
                    disabled={isLoading}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 border border-gray-200"
                    aria-label="Refresh"
                    title="Refresh"
                  >
                    <RefreshCw
                      className={`w-5 h-5 text-gray-600 ${isLoading ? "animate-spin" : ""}`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {viewMode === "list" ? (
              /* List View */
              <div className="space-y-4">
                {/* Loading State */}
                {isLoading && listings.length === 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
                      <p className="text-gray-600">{t("overviewLoading")}</p>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {error && listings.length === 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-red-200 p-12">
                    <div className="text-center">
                      <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        {t("overviewErrorTitle")}
                      </h2>
                      <p className="text-red-600 mb-4">{error}</p>
                      <button
                        onClick={refresh}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        {t("overviewRetry")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Listings Grid */}
                {listings.length > 0 && (
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
                    onScroll={handleScroll}
                  >
                    {listings.map((listing) => (
                      <ListingCard
                        key={listing.id}
                        listing={listing}
                        onClick={handleListingClick}
                        onHover={handleListingHover}
                        isSelected={selectedListing?.id === listing.id}
                      />
                    ))}
                  </div>
                )}

                {/* Load More */}
                {hasMore && !isLoading && (
                  <div className="text-center py-4">
                    <button
                      onClick={loadMore}
                      disabled={isLoadingMore}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {isLoadingMore && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      {isLoadingMore
                        ? t("overviewLoadingMore")
                        : t("overviewLoadMore")}
                    </button>
                  </div>
                )}

                {/* Empty State */}
                {!isLoading && !error && listings.length === 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="w-10 h-10 text-gray-400" />
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        {t("overviewEmptyTitle")}
                      </h2>
                      <p className="text-gray-500 max-w-md mx-auto">
                        {t("overviewEmptyDesc")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Desktop Map View - Fixed height, page scrollable */
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <ListingsMap
                  listings={listings}
                  selectedListing={selectedListing}
                  onListingSelect={handleListingClick}
                  onListingHover={handleListingHover}
                  onListingClick={handleListingClick}
                  isMobile={false}
                  className="h-[calc(100vh-280px)] min-h-[500px]"
                />
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
